import type { Maybe } from "@entrilia/shared/types";
import {
  convertISODateShort,
  formatDateShort,
  formatDateTimeShort,
  isValidDate,
} from "@entrilia/shared/utilities/dateUtils";
import { getUserLocaleInfo } from "@entrilia/shared/utilities/userLocaleConfig";
import { format, isMatch, isValid, parse, parseISO } from "date-fns";
import { FormatType } from "../../../../api/types/fieldConfigurationTypes";
import {
  getRelativeDateLabel,
  isRelativeDateValue,
  isRelativeDayValue,
  isRelativePeriodValue,
  resolveRelativeDay,
  resolveRelativePeriod,
} from "./relativeDates";

export const DATE_ISO_FORMAT = "yyyy-MM-dd";
export const DATE_US_FORMAT = "MM/dd/yyyy";
export const DATE_TIME_ISO_FORMAT = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'";
const ISO_DATE_TIME_PREFIX_REGEX = /^(\d{4}-\d{2}-\d{2})T/;
const STRICT_ISO_DATE_TIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:(?:Z|[+-]\d{2}:\d{2}))?$/;

const parseIsoDatePrefix = (value: string): Date | null => {
  const datePrefix = value.match(ISO_DATE_TIME_PREFIX_REGEX)?.[1];
  if (!datePrefix || !isMatch(datePrefix, DATE_ISO_FORMAT)) {
    return null;
  }

  return parse(datePrefix, DATE_ISO_FORMAT, new Date());
};

export const getCurrentLocale = () => {
  return getUserLocaleInfo().locale;
};

export const getValidatedDateString = (value: Maybe<string>, formatType: FormatType | undefined) => {
  if (value === undefined || value === null) {
    return "";
  }
  if (isRelativeDateValue(value)) {
    return getRelativeDateLabel(value);
  }
  if (isMatch(value, DATE_ISO_FORMAT)) {
    return formatDateShort(parse(value, DATE_ISO_FORMAT, new Date()));
  }
  if (isMatch(value, DATE_US_FORMAT)) {
    return formatDateShort(parse(value, DATE_US_FORMAT, new Date()));
  }

  if (formatType !== FormatType.DateTime) {
    return convertISODateShort(value);
  }

  const day = getValidatedDate(value);
  return day && isValid(day) ? formatDateShort(day) : "";
};

export const getValidatedDate = (value: Maybe<string>) => {
  if (value === undefined || value === null || isRelativeDateValue(value)) {
    return null;
  }
  if (isMatch(value, DATE_ISO_FORMAT)) {
    return parse(value, DATE_ISO_FORMAT, new Date());
  }
  if (isMatch(value, DATE_US_FORMAT)) {
    return parse(value, DATE_US_FORMAT, new Date());
  }

  if (value.includes("T") && !STRICT_ISO_DATE_TIME_REGEX.test(value)) {
    return new Date(NaN);
  }

  const parsedIsoDate = parseISO(value);
  if (!isValid(parsedIsoDate)) {
    return parsedIsoDate;
  }
  const parsedDatePrefix = parseIsoDatePrefix(value);
  if (parsedDatePrefix && value.includes("T")) {
    return parsedDatePrefix;
  }
  return parsedIsoDate;
};

export const toFormattedDateString = (value: Date | undefined, formatType: FormatType | undefined): string => {
  if (value === undefined) {
    return "";
  }
  if (formatType === FormatType.Date || formatType === undefined) {
    return formatDateShort(value);
  } else if (formatType === FormatType.DateTime) {
    return formatDateTimeShort(value);
  }
  return "";
};

export const getRelativeDateResolvedString = (
  value: Maybe<string>,
  formatType: FormatType | undefined,
  now: Date = new Date()
): string | undefined => {
  if (isRelativeDayValue(value)) {
    return toFormattedDateString(resolveRelativeDay(value, now), formatType);
  }
  if (isRelativePeriodValue(value)) {
    const [from, to] = resolveRelativePeriod(value, now);
    return `${toFormattedDateString(from, formatType)} - ${toFormattedDateString(to, formatType)}`;
  }
  return undefined;
};

/** If the value is a US date (MM/dd/yyyy), converts it to ISO (yyyy-MM-dd). Otherwise returns the value as-is. */
export const convertUsDateToIso = (value: Maybe<string>): string | null => {
  if (value === undefined || value === null) {
    return null;
  }
  if (isMatch(value ?? "", DATE_US_FORMAT)) {
    const date = parse(value, DATE_US_FORMAT, new Date());
    return format(date, DATE_ISO_FORMAT);
  }
  return value;
};

export const formatToISODate = (value: Date) => {
  return isValidDate(value) ? format(value, DATE_ISO_FORMAT) : "";
};
